import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';
import { STUDY_KEY_TITLE } from '../constant';
import { Study } from '../types';
import styles from './styles';

/* eslint-disable no-unused-vars */
interface StudyComparisonBlockProps {
  slotKey: string | number;
  displayIndex: number;
  prevStudy: Study | null;
  currStudy: Study | null;
  rowStudyId: string | number;
  canCopyStudy: boolean;
  studyCopying: boolean;
  isCopying: boolean;
  onCopyStudyClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // eslint-disable-next-line react/require-default-props
  onEditStudyClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // eslint-disable-next-line react/require-default-props
  onSaveStudy?: () => void;
  editingStudyId: number | string | null;
  studyKeysOrder: string[];
  attributeNames: string[];
  formatStudyValue: (key: string, value: unknown) => string;
  getAttributeValue: (study: Study | null, attributeName: string) => string;
  renderCurrentCellForStudyKey: (
    key: string,
    currStudy: Study | null,
    studyId: string | number
  ) => React.ReactNode;
  renderCurrentCellForAttribute: (attrName: string, currStudy: Study | null) => React.ReactNode;
}
/* eslint-enable no-unused-vars */

const StudyComparisonBlock = ({
  slotKey,
  displayIndex,
  prevStudy,
  currStudy,
  rowStudyId,
  canCopyStudy,
  studyCopying,
  isCopying,
  onCopyStudyClick,
  onEditStudyClick,
  onSaveStudy,
  editingStudyId,
  studyKeysOrder,
  attributeNames,
  formatStudyValue,
  getAttributeValue,
  renderCurrentCellForStudyKey,
  renderCurrentCellForAttribute
}: StudyComparisonBlockProps): React.ReactElement => {
  return (
    <Paper variant='outlined' sx={styles.studyPaper}>
      <Box sx={styles.studyPaperHeader}>
        <Typography variant='subtitle1'>Study {displayIndex + 1}</Typography>
        <Box display='flex' gap={1}>
          {canCopyStudy && (
            <Button
              size='small'
              variant='outlined'
              data-slot-key={String(slotKey)}
              onClick={onCopyStudyClick}
              disabled={studyCopying || isCopying}>
              {studyCopying ? 'Copying...' : 'Copy study'}
            </Button>
          )}
          {currStudy != null && onEditStudyClick && editingStudyId !== currStudy.id && (
            <Button
              size='small'
              variant='outlined'
              color='primary'
              data-slot-key={String(slotKey)}
              onClick={onEditStudyClick}>
              Edit
            </Button>
          )}
          {currStudy != null && onSaveStudy != null && editingStudyId === currStudy.id && (
            <Button size='small' variant='contained' color='primary' onClick={onSaveStudy}>
              Save
            </Button>
          )}
        </Box>
      </Box>
      <TableContainer>
        <Table size='small' aria-label={`Study ${slotKey}`}>
          <TableBody>
            {studyKeysOrder.map((key: string) => (
              <TableRow key={`study-${slotKey}-${key}`} sx={styles.studyRow}>
                <TableCell component='th' scope='row' sx={styles.studyCellTh}>
                  {STUDY_KEY_TITLE[key]?.title ?? key}
                </TableCell>
                <TableCell sx={styles.studyCellPrev}>
                  {formatStudyValue(key, prevStudy?.[key])}
                </TableCell>
                <TableCell sx={styles.studyCellCurr}>
                  {renderCurrentCellForStudyKey(key, currStudy, rowStudyId)}
                </TableCell>
              </TableRow>
            ))}
            {attributeNames.map((attrName: string) => (
              <TableRow key={`attr-${slotKey}-${attrName}`} sx={styles.studyRow}>
                <TableCell component='th' scope='row' sx={styles.studyCellTh}>
                  {attrName}
                </TableCell>
                <TableCell sx={styles.studyCellPrev}>
                  {getAttributeValue(prevStudy, attrName)}
                </TableCell>
                <TableCell sx={styles.studyCellCurr}>
                  {renderCurrentCellForAttribute(attrName, currStudy)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default StudyComparisonBlock;
